import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useFinanceStore, useAccountFilterStore, useAuthStore } from '../store';
import { formatCurrency, formatDate, parseLocalDate, getStatusBadgeClass, getStatusLabel, isOverdue } from '../lib/utils';
import { Button } from '../components/ui/button';
import NotesCell from '../components/NotesCell';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Search, Filter, Loader2, Edit, DollarSign, FileText, Building2, Percent, Calculator, Eye, Trash2, ChevronLeft, ChevronRight, Calendar, Copy, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, Download, X, RotateCcw, Layers, Wrench } from 'lucide-react';
import axios from 'axios';
import ColumnSelector from '../components/ColumnSelector';
import BulkReceiveDialog from '../components/BulkReceiveDialog';
import { AdvancedFilters, applyAdvancedFilters } from '../components/AdvancedFilters';
import { exportToExcel, EXPORT_CONFIGS } from '../lib/exportExcel';
import { generateLedgerPdf } from '../lib/ledgerPdf';
import ValidateExportDialog from '../components/ValidateExportDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  ActiveFilterChips, MonthNavigator, SearchInput, ExportDropdown,
  AdminToolsDropdown, buildFilterChips,
  ClickableSummaryCards, QuickPresets, QuickFilters, GroupByDropdown,
} from '../components/LedgerToolbar';
import { applyLedgerClientFilters, groupLedgerItems } from '../hooks/useLedgerFilters';

import { API_URL } from '../lib/api';

// Helper to get current month in YYYY-MM format
const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Helper to format month for display
const formatMonth = (monthStr) => {
  const [year, month] = monthStr.split('-');
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return `${months[parseInt(month) - 1]} ${year}`;
};

// Helper to change month
const changeMonth = (monthStr, delta) => {
  const [year, month] = monthStr.split('-').map(Number);
  let newMonth = month + delta;
  let newYear = year;
  if (newMonth > 12) { newMonth = 1; newYear++; }
  if (newMonth < 1) { newMonth = 12; newYear--; }
  return `${newYear}-${String(newMonth).padStart(2, '0')}`;
};

// Constants
const OPERATION_NATURES = [
  { value: 'service', label: 'Serviço' },
  { value: 'rental', label: 'Locação' },
  { value: 'sale', label: 'Venda' },
];

const ISS_RETENTION_TYPES = [
  { value: 'not_retained', label: 'Não retido' },
  { value: 'fully_retained', label: 'Retido total (100%)' },
  { value: 'partially_retained', label: 'Retido parcial' },
  { value: 'substitution', label: 'Substituição tributária' },
];

// Column definitions for visibility toggle
const RECEIVABLE_COLUMNS = [
  { key: 'obs', label: 'Obs', essential: false },
  { key: 'checkbox', label: 'Seleção', essential: false },
  // Nova ordem solicitada (2026-06-10): Competência primeiro (vinda da OS), depois
  // dados do documento, depois datas de vencimento/recebimento.
  // Competência = mês/ano do serviço (period_start da OS), NUNCA o vencimento ou emissão da NF.
  { key: 'competence_date', label: 'Competência', essential: true },
  { key: 'description', label: 'Descrição', essential: true },
  { key: 'invoice_number', label: 'Nº Doc', essential: false },
  { key: 'nf_number', label: 'Nº NF', essential: false },
  { key: 'category', label: 'Categoria', essential: false },
  { key: 'gross_amount', label: 'Bruto', essential: true },
  { key: 'due_date', label: 'Vencimento', essential: true },
  { key: 'forecast_date', label: 'Previsão', essential: false },
  { key: 'payment_date', label: 'Recebimento', essential: false },
  { key: 'customer_name', label: 'Cliente', essential: true },
  { key: 'nature', label: 'Natureza', essential: false },
  { key: 'bank_account', label: 'Conta', essential: false },
  { key: 'iss_retained', label: 'ISS Ret.', essential: false },
  { key: 'net_amount', label: 'Líquido', essential: true },
  { key: 'received_amount', label: 'Recebido', essential: false },
  { key: 'status', label: 'Status', essential: true },
  { key: 'actions', label: 'Ações', essential: true },
];

// Get initial visible columns from localStorage or default to ESSENTIAL + mais usadas
// (Previsão, Recebimento, Nº Doc, Natureza, Bruto, ISS Ret, Recebido ficam ocultas por padrão — habilite em "Colunas")
const DEFAULT_VISIBLE_RECEIVABLE = RECEIVABLE_COLUMNS.filter(c => c.essential).map(c => c.key).concat(['obs', 'checkbox', 'category', 'nf_number']);
const RECEIVABLE_SCHEMA_VERSION = '2026-06-competence-first';
const getInitialVisibleColumns = () => {
  const savedVersion = localStorage.getItem('receivables_columns_schema');
  const saved = localStorage.getItem('receivables_visible_columns');
  if (savedVersion !== RECEIVABLE_SCHEMA_VERSION) {
    localStorage.setItem('receivables_columns_schema', RECEIVABLE_SCHEMA_VERSION);
    localStorage.setItem('receivables_visible_columns', JSON.stringify(DEFAULT_VISIBLE_RECEIVABLE));
    return DEFAULT_VISIBLE_RECEIVABLE;
  }
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      return DEFAULT_VISIBLE_RECEIVABLE;
    }
  }
  return DEFAULT_VISIBLE_RECEIVABLE;
};

// Initial form state
const initialFormData = {
  customer_id: '',
  customer_name: '',
  description: '',
  operation_nature: 'service',
  competence_date: '',
  invoice_date: '',
  invoice_number: '',
  nf_number: '',
  due_date: '',
  forecast_date: '', // Data de previsão para fluxo de caixa
  gross_amount: '',
  category_id: '',
  cost_center_id: '', // Centro de custo
  bank_account_id: '', // Conta bancária para recebimento
  notes: '',
  budget_number: '',
  // ISS fields
  iss_enabled: false,
  iss_retention_type: 'not_retained',
  iss_municipality: '',
  iss_base_amount: '',
  iss_rate: '',
  iss_retained_amount: '',
  iss_receipt_number: '',
  // Installment fields
  is_installment: false,
  num_installments: '2',
};

export const ReceivablesPage = () => {
  const {
    receivables, receivablesLoading, fetchReceivables,
    createReceivable, updateReceivable, receiveReceivable,
    customers, categories, fetchCustomers, fetchCategories,
    createCustomer, token, bankAccounts, fetchBankAccounts
  } = useFinanceStore();
  
  const { user, tenant } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  
  const { getFilterParam, allSelected, selectedAccountIds } = useAccountFilterStore();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isReceiveOpen, setIsReceiveOpen] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isReplicateOpen, setIsReplicateOpen] = useState(false);
  const [isConvertToInstallmentsOpen, setIsConvertToInstallmentsOpen] = useState(false);
  const [costCenters, setCostCenters] = useState([]);
  const [isCreateCostCenterOpen, setIsCreateCostCenterOpen] = useState(false);
  const [newCostCenterName, setNewCostCenterName] = useState('');
  const [convertInstallmentsData, setConvertInstallmentsData] = useState({
    num_installments: '2',
    first_due_date: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState(() => localStorage.getItem('receivables_filterStatus') || 'unpaid');
  const [filterMonth, setFilterMonth] = useState(() => localStorage.getItem('receivables_filterMonth') || getCurrentMonth());
  const [filterMonthRange, setFilterMonthRange] = useState(() => {
    const v = localStorage.getItem('receivables_filterMonthRange');
    return v !== null ? parseInt(v) : 0;
  });
  const [dateFilterField, setDateFilterField] = useState(() => localStorage.getItem('receivables_dateFilterField') || 'due_date');
  const [customStartDate, setCustomStartDate] = useState(() => localStorage.getItem('receivables_customStart') || '');
  const [customEndDate, setCustomEndDate] = useState(() => localStorage.getItem('receivables_customEnd') || '');
  const [hideTinyRendimentos, setHideTinyRendimentos] = useState(() => localStorage.getItem('receivables_hideRendimentos') !== 'false');
  const [exportValidatorOpen, setExportValidatorOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // ==== Novos filtros rápidos ====
  const [customerFilter, setCustomerFilter] = useState(() => localStorage.getItem('receivables_customerFilter') || 'all');
  const [categoryFilter, setCategoryFilter] = useState(() => localStorage.getItem('receivables_categoryFilter') || 'all');
  const [costCenterFilter, setCostCenterFilter] = useState(() => localStorage.getItem('receivables_costCenterFilter') || 'all');
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem('receivables_groupBy') || 'none');
  // Default: abrir mostrando Vencidos + Vence hoje. Usuário pode desativar clicando no chip.
  const [quickPreset, setQuickPreset] = useState(() => {
    const v = localStorage.getItem('receivables_quickPreset');
    return v === null ? 'overdue_today' : (v === '__null__' ? null : v);
  });

  useEffect(() => { localStorage.setItem('receivables_filterStatus', filterStatus); }, [filterStatus]);
  useEffect(() => { localStorage.setItem('receivables_filterMonth', filterMonth); }, [filterMonth]);
  useEffect(() => { localStorage.setItem('receivables_filterMonthRange', String(filterMonthRange)); }, [filterMonthRange]);
  useEffect(() => { localStorage.setItem('receivables_dateFilterField', dateFilterField); }, [dateFilterField]);
  useEffect(() => { localStorage.setItem('receivables_customStart', customStartDate); }, [customStartDate]);
  useEffect(() => { localStorage.setItem('receivables_customEnd', customEndDate); }, [customEndDate]);
  useEffect(() => { localStorage.setItem('receivables_hideRendimentos', String(hideTinyRendimentos)); }, [hideTinyRendimentos]);

  // Filtro client-side: esconde rendimentos bancários pequenos (REND PAGO APLIC) < R$ 5
  const RENDIMENTO_REGEX = /REND\s*PAGO\s*APLIC|RENDIM|APLICACAO|APLICAÇÃO|REND\s*MAIS/i;
  const isTinyRendimento = (r) => {
    if (!hideTinyRendimentos) return false;
    if (!r) return false;
    const desc = (r.description || '') + ' ' + (r.notes || '');
    if (!RENDIMENTO_REGEX.test(desc)) return false;
    const amt = Math.abs(r.amount || 0);
    return amt < 5; // R$ 0,01 a R$ 4,99
  };
  useEffect(() => { localStorage.setItem('receivables_customerFilter', customerFilter); }, [customerFilter]);
  useEffect(() => { localStorage.setItem('receivables_categoryFilter', categoryFilter); }, [categoryFilter]);
  useEffect(() => { localStorage.setItem('receivables_costCenterFilter', costCenterFilter); }, [costCenterFilter]);
  useEffect(() => { localStorage.setItem('receivables_groupBy', groupBy); }, [groupBy]);
  useEffect(() => { localStorage.setItem('receivables_quickPreset', quickPreset === null ? '__null__' : quickPreset); }, [quickPreset]);

  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [replicateDate, setReplicateDate] = useState('');
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'due_date', direction: 'asc' });
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);
  const isColumnVisible = (key) => visibleColumns.includes(key);
  
  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState({});
  
  // Date field options for filter dropdown
  const dateFieldOptions = [
    { value: 'due_date', label: 'Vencimento' },
    { value: 'received_date', label: 'Recebimento' },
    { value: 'forecast_date', label: 'Previsão' },
  ];
  
  // Filter columns config for advanced filters
  const filterableColumns = useMemo(() => [
    { key: 'due_date', label: 'Vencimento' },
    { key: 'forecast_date', label: 'Previsão' },
    { key: 'payment_date', label: 'Recebimento' },
    { key: 'customer_name', label: 'Cliente' },
    { key: 'description', label: 'Descrição' },
    { key: 'category', label: 'Categoria' },
    { key: 'net_expected_amount', label: 'Valor' },
  ], []);

  const dateColumns = ['due_date', 'forecast_date', 'payment_date'];
  
  const [formData, setFormData] = useState(initialFormData);
  
  const [paymentData, setPaymentData] = useState({
    payment_date: '',
    received_amount: '',
    bank_account_id: '',
    discount: '0',
    interest_penalty: '0',
    notes: '',
    // Campos para pagamento parcial
    remaining_balance_due_date: '',
    remaining_balance_forecast: '',
    // Opção para não criar transação bancária automática
    skip_bank_transaction: false
  });

  // Local state for comments - for inline editing
  const [localComments, setLocalComments] = useState({});

  // Save comment to backend
  const saveComment = async (receivableId, text) => {
    try {
      await axios.patch(`${API_URL}/api/finance/receivables/${receivableId}/comment`, 
        { notes: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update via fetchReceivables to keep state in sync
      fetchReceivables();
    } catch (error) {
      console.error('Error saving comment:', error);
      toast.error('Erro ao salvar comentário');
    }
  };

  const [newCustomer, setNewCustomer] = useState({
    name: '',
    trade_name: '',
    document: '',
    email: '',
    phone: '',
    cep: '',
    address: '',
    address_number: '',
    address_complement: '',
    neighborhood: '',
    city: '',
    state: '',
    iss_retention_default: false,
    iss_municipality: '',
    iss_default_rate: ''
  });
  const [isLookingUpCnpj, setIsLookingUpCnpj] = useState(false);
  const [isLookingUpCep, setIsLookingUpCep] = useState(false);

  const [newCategoryName, setNewCategoryName] = useState('');

  // Multi-select state
  const [selectedItems, setSelectedItems] = useState([]);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isBulkReceiveOpen, setIsBulkReceiveOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    category_id: '',
    forecast_date: '',
    competence_date: '',
    notes: ''
  });

  // Installment edit state
  const [isInstallmentEditOpen, setIsInstallmentEditOpen] = useState(false);
  const [installmentEditData, setInstallmentEditData] = useState({
    scope: 'single',
    description: '',
    customer_name: '',
    category_id: '',
    cost_center_id: '',
    amount: '',
    notes: ''
  });

  // CNPJ Lookup function
  const handleCnpjLookup = async (cnpj) => {
    if (!cnpj || cnpj.replace(/\D/g, '').length < 14) return;
    
    setIsLookingUpCnpj(true);
    try {
      const response = await axios.get(`${API_URL}/api/lookup/cnpj/${cnpj}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      const data = response.data;
      
      if (data.success) {
        setNewCustomer(prev => ({
          ...prev,
          name: data.razao_social || prev.name,
          trade_name: data.nome_fantasia || '',
          email: data.email || prev.email,
          phone: data.telefone || prev.phone,
          cep: data.cep || prev.cep,
          address: data.logradouro || prev.address,
          address_number: data.numero || prev.address_number,
          address_complement: data.complemento || prev.address_complement,
          neighborhood: data.bairro || prev.neighborhood,
          city: data.municipio || prev.city,
          state: data.uf || prev.state,
          iss_municipality: data.municipio && data.uf ? `${data.municipio}/${data.uf}` : prev.iss_municipality
        }));
        toast.success('Dados do CNPJ carregados!');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('CNPJ não encontrado');
      } else if (error.response?.status === 429) {
        toast.error('Limite de consultas excedido. Tente novamente em alguns minutos.');
      } else {
        toast.error('Erro ao consultar CNPJ');
      }
    } finally {
      setIsLookingUpCnpj(false);
    }
  };

  // CEP Lookup function
  const handleCepLookup = async (cep) => {
    if (!cep || cep.replace(/\D/g, '').length < 8) return;
    
    setIsLookingUpCep(true);
    try {
      const response = await axios.get(`${API_URL}/api/lookup/cep/${cep}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      const data = response.data;
      
      if (data.success) {
        setNewCustomer(prev => ({
          ...prev,
          address: data.logradouro || prev.address,
          neighborhood: data.bairro || prev.neighborhood,
          city: data.cidade || prev.city,
          state: data.uf || prev.state,
          iss_municipality: data.cidade && data.uf ? `${data.cidade}/${data.uf}` : prev.iss_municipality
        }));
        toast.success('Endereço carregado!');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('CEP não encontrado');
      } else {
        toast.error('Erro ao consultar CEP');
      }
    } finally {
      setIsLookingUpCep(false);
    }
  };

  // Reset form when opening create dialog
  useEffect(() => {
    if (isCreateOpen) {
      // Force reset to initial state when dialog opens
      setFormData({...initialFormData});
    }
  }, [isCreateOpen]);
  
  // Also reset when closing the dialog
  const handleCreateDialogChange = (open) => {
    if (!open) {
      // Reset form when closing
      setFormData({...initialFormData});
    }
    setIsCreateOpen(open);
  };

  useEffect(() => {
    // Build params based on filter mode: custom range (-1), Todos (0), or month range
    let params;
    if (filterMonthRange === -1) {
      params = { date_filter_field: dateFilterField, limit: 100000 };
      if (customStartDate) params.start_date = customStartDate;
      if (customEndDate) params.end_date = customEndDate;
    } else if (filterMonthRange !== 0) {
      params = { month: filterMonth, month_range: filterMonthRange, date_filter_field: dateFilterField };
    } else {
      params = { date_filter_field: dateFilterField, limit: 100000 };
    }
    if (filterStatus !== 'all') params.status = filterStatus;
    
    // Apply bank account filter from global state
    const accountFilter = getFilterParam();
    if (accountFilter) {
      params.account_ids = accountFilter;
    }
    
    fetchReceivables(params);
    fetchCustomers();
    fetchCategories('income');
    fetchBankAccounts();
    fetchCostCenters();
  }, [fetchReceivables, fetchCustomers, fetchCategories, fetchBankAccounts, filterStatus, filterMonth, filterMonthRange, dateFilterField, customStartDate, customEndDate, allSelected, selectedAccountIds]);

  const fetchCostCenters = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/finance/cost-centers`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data;
      setCostCenters(Array.isArray(data) ? data : (data.cost_centers || []));
    } catch (e) { console.error(e); }
  };

  const handleCreateCostCenter = async () => {
    if (!newCostCenterName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/finance/cost-centers`, { name: newCostCenterName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Centro de custo criado');
      setNewCostCenterName('');
      setIsCreateCostCenterOpen(false);
      fetchCostCenters();
    } catch (e) {
      toast.error('Erro ao criar centro de custo');
    }
  };

  // Calculate ISS retained amount when rate or base changes
  useEffect(() => {
    if (formData.iss_enabled && formData.iss_retention_type !== 'not_retained') {
      const base = parseFloat(formData.iss_base_amount) || 0;
      const rate = parseFloat(formData.iss_rate) || 0;
      
      if (formData.iss_retention_type === 'fully_retained') {
        const calculated = (base * rate / 100).toFixed(2);
        setFormData(prev => ({ ...prev, iss_retained_amount: calculated }));
      }
    }
  }, [formData.iss_base_amount, formData.iss_rate, formData.iss_retention_type, formData.iss_enabled]);

  // Auto-fill ISS base from gross amount - update when gross_amount changes
  useEffect(() => {
    if (formData.iss_enabled && formData.gross_amount) {
      // Auto-fill ISS base with gross amount when:
      // 1. ISS is enabled AND
      // 2. We have a gross amount AND  
      // 3. Either base is empty OR base equals the previous gross amount (user hasn't manually changed it)
      setFormData(prev => {
        // Only auto-update if base is empty or equals gross (meaning it was auto-filled before)
        const shouldAutoUpdate = !prev.iss_base_amount || 
          prev.iss_base_amount === prev.gross_amount ||
          prev.iss_base_amount === '';
        
        if (shouldAutoUpdate) {
          return { ...prev, iss_base_amount: prev.gross_amount };
        }
        return prev;
      });
    }
  }, [formData.iss_enabled, formData.gross_amount]);

  // Apply customer ISS defaults when customer changes
  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customer_id: customerId,
        customer_name: customer.name,
        iss_enabled: customer.iss_retention_default || false,
        iss_retention_type: customer.iss_retention_default ? 'fully_retained' : 'not_retained',
        iss_municipality: customer.iss_municipality || '',
        iss_rate: customer.iss_default_rate ? String(customer.iss_default_rate) : ''
      }));
    }
  };

  // Calculate net expected amount
  const netExpectedAmount = useMemo(() => {
    const gross = parseFloat(formData.gross_amount) || 0;
    const retained = parseFloat(formData.iss_retained_amount) || 0;
    return gross - retained;
  }, [formData.gross_amount, formData.iss_retained_amount]);

  // Calculate payment difference
  const paymentDifference = useMemo(() => {
    if (!selectedReceivable) return 0;
    const expected = selectedReceivable.net_expected_amount || selectedReceivable.amount || 0;
    const received = parseFloat(paymentData.received_amount) || 0;
    const discount = parseFloat(paymentData.discount) || 0;
    const interest = parseFloat(paymentData.interest_penalty) || 0;
    return (received + discount - interest) - expected;
  }, [selectedReceivable, paymentData]);

  // Calculate remaining balance for partial payment
  const remainingBalance = useMemo(() => {
    if (!selectedReceivable) return 0;
    const expected = selectedReceivable.net_expected_amount || selectedReceivable.gross_amount || selectedReceivable.amount || 0;
    const received = parseFloat(paymentData.received_amount) || 0;
    const discount = parseFloat(paymentData.discount) || 0;
    const interest = parseFloat(paymentData.interest_penalty) || 0;
    const balance = expected - received - discount + interest;
    return balance > 0.01 ? balance : 0;
  }, [selectedReceivable, paymentData]);

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/finance/categories`, {
        name: newCategoryName,
        type: 'income'
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success('Categoria criada!');
      setNewCategoryName('');
      setIsCategoryModalOpen(false);
      fetchCategories('income');
      if (res.data.id) {
        setFormData(prev => ({ ...prev, category_id: res.data.id }));
      }
    } catch (error) {
      toast.error('Erro ao criar categoria');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validação obrigatória: cost_center_id
    if (!formData.cost_center_id) {
      toast.error('Centro de Custo é obrigatório');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const taxes = [];
      if (formData.iss_enabled && formData.operation_nature === 'service') {
        taxes.push({
          tax_type: 'ISS',
          retention_type: formData.iss_retention_type,
          municipality: formData.iss_municipality,
          base_amount: parseFloat(formData.iss_base_amount) || null,
          rate: parseFloat(formData.iss_rate) || null,
          retained_amount: parseFloat(formData.iss_retained_amount) || null,
          receipt_number: formData.iss_receipt_number || null
        });
      }

      // Check if installment mode
      if (formData.is_installment) {
        const numInstallments = parseInt(formData.num_installments) || 2;
        
        const response = await axios.post(`${API_URL}/api/finance/receivables/installments`, {
          description: formData.description,
          customer_id: formData.customer_id || null,
          customer_name: formData.customer_name,
          operation_nature: formData.operation_nature,
          competence_date: formData.competence_date || null,
          invoice_date: formData.invoice_date || null,
          first_due_date: formData.due_date,
          gross_amount: parseFloat(formData.gross_amount),
          num_installments: numInstallments,
          category_id: formData.category_id || null,
          cost_center_id: formData.cost_center_id || null,
          bank_account_id: formData.bank_account_id || null,
          notes: formData.notes || null,
          budget_number: formData.budget_number || null,
          taxes: taxes.length > 0 ? taxes : null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(`${numInstallments} parcela(s) criada(s) com sucesso!`);
        fetchReceivables({ month: filterMonth, month_range: filterMonthRange });
        handleCreateDialogChange(false);  // Close and reset form
      } else {
        await createReceivable({
          description: formData.description,
          customer_id: formData.customer_id || null,
          customer_name: formData.customer_name,
          operation_nature: formData.operation_nature,
          competence_date: formData.competence_date || null,
          invoice_date: formData.invoice_date || null,
          invoice_number: formData.invoice_number || null,
          nf_number: formData.nf_number || null,
          due_date: formData.due_date,
          forecast_date: formData.forecast_date || null,
          gross_amount: parseFloat(formData.gross_amount),
          category_id: formData.category_id || null,
          cost_center_id: formData.cost_center_id || null,
          bank_account_id: formData.bank_account_id || null,
          notes: formData.notes || null,
          taxes: taxes.length > 0 ? taxes : null
        });
        
        toast.success('Conta a receber criada com sucesso!');
        fetchReceivables({ month: filterMonth, month_range: filterMonthRange, date_filter_field: dateFilterField });
      }
      
      handleCreateDialogChange(false);  // This will also reset the form
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta a receber');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedReceivable) return;
    
    // Validação obrigatória
    if (!formData.cost_center_id) {
      toast.error('Centro de Custo é obrigatório');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const taxes = [];
      if (formData.iss_enabled && formData.operation_nature === 'service') {
        taxes.push({
          tax_type: 'ISS',
          retention_type: formData.iss_retention_type,
          municipality: formData.iss_municipality,
          base_amount: parseFloat(formData.iss_base_amount) || null,
          rate: parseFloat(formData.iss_rate) || null,
          retained_amount: parseFloat(formData.iss_retained_amount) || null,
          receipt_number: formData.iss_receipt_number || null
        });
      }

      await updateReceivable(selectedReceivable.id, {
        description: formData.description,
        customer_id: formData.customer_id || null,
        customer_name: formData.customer_name,
        operation_nature: formData.operation_nature,
        competence_date: formData.competence_date || null,
        invoice_date: formData.invoice_date || null,
        invoice_number: formData.invoice_number || null,
        nf_number: formData.nf_number || null,
        due_date: formData.due_date,
        forecast_date: formData.forecast_date || null,
        gross_amount: parseFloat(formData.gross_amount),
        category_id: formData.category_id || null,
        cost_center_id: formData.cost_center_id || null,
        bank_account_id: formData.bank_account_id || null,
        notes: formData.notes || null,
        taxes: taxes.length > 0 ? taxes : null
      });
      
      toast.success('Conta a receber atualizada!');
      setIsEditOpen(false);
      setSelectedReceivable(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReceive = async (e) => {
    e.preventDefault();
    if (!selectedReceivable || !paymentData.bank_account_id) return;

    // VALIDAÇÃO (e): Categoria "Aguardando..." em status Recebido — avisa antes
    const cat = (selectedReceivable.category || '').toLowerCase();
    if (cat.includes('aguardando') || cat.includes('aguard')) {
      const ok = window.confirm(
        `⚠ ATENÇÃO: Este título está com categoria "${selectedReceivable.category}" — uma categoria de triagem.\n\n` +
        `Antes de marcar como Recebido, é recomendado reclassificar pra uma categoria final (ex: Locação, Serviço Técnico, Estúdio, Eventos, etc).\n\n` +
        `Continuar mesmo assim?`
      );
      if (!ok) return;
    }
    
    // Re-fetch the receivable to get the latest status (prevents stale state issues after replicate)
    try {
      const freshRes = await axios.get(`${API_URL}/api/finance/receivables/${selectedReceivable.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const freshReceivable = freshRes.data;
      if (freshReceivable.status === 'received') {
        toast.error('Este título já foi recebido. A lista foi atualizada.');
        setIsReceiveOpen(false);
        setSelectedReceivable(null);
        fetchReceivables();
        return;
      }
    } catch (fetchErr) {
      toast.error('Erro ao verificar status da conta. Tente novamente.');
      setIsReceiveOpen(false);
      return;
    }
    
    setIsSubmitting(true);
    try {
      const result = await receiveReceivable(selectedReceivable.id, {
        payment_date: paymentData.payment_date,
        received_amount: parseFloat(paymentData.received_amount),
        bank_account_id: paymentData.bank_account_id,
        discount: parseFloat(paymentData.discount) || 0,
        interest_penalty: parseFloat(paymentData.interest_penalty) || 0,
        notes: paymentData.notes || null,
        // Campos para pagamento parcial
        remaining_balance_due_date: paymentData.remaining_balance_due_date || null,
        remaining_balance_forecast: paymentData.remaining_balance_forecast || null,
        // Opção para não criar transação bancária automática
        skip_bank_transaction: paymentData.skip_bank_transaction
      });
      
      // Check if a remaining balance was created
      if (result?.created_remaining_balance) {
        toast.success(`Recebimento registrado! Saldo devedor de ${formatCurrency(result.remaining_balance_amount)} criado automaticamente.`, {
          duration: 5000
        });
      } else {
        toast.success('Recebimento registrado!');
      }
      
      setIsReceiveOpen(false);
      setSelectedReceivable(null);
      setPaymentData({ 
        payment_date: '', 
        received_amount: '', 
        bank_account_id: '', 
        discount: '0', 
        interest_penalty: '0', 
        notes: '',
        remaining_balance_due_date: '',
        remaining_balance_forecast: '',
        skip_bank_transaction: false
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao registrar recebimento';
      toast.error(errorMsg);
      // If already received error, refresh the list
      if (errorMsg.toLowerCase().includes('já') && errorMsg.toLowerCase().includes('receb')) {
        fetchReceivables();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (receivable) => {
    setSelectedReceivable(receivable);
    
    // Always open regular edit modal for full editing capabilities
    // For installments, we just need to use the regular form
    const issTax = receivable.taxes?.find(t => t.tax_type === 'ISS');
    // Normalize ISO date-time strings to YYYY-MM-DD (input[type=date] silently
    // rejects anything with time component, appearing blank and breaking validation).
    const toDateOnly = (d) => {
      if (!d) return '';
      if (typeof d === 'string') return d.slice(0, 10);
      try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
    };
    setFormData({
      customer_id: receivable.customer_id || '',
      customer_name: receivable.customer_name || '',
      description: receivable.description || '',
      operation_nature: receivable.operation_nature || 'service',
      competence_date: toDateOnly(receivable.competence_date),
      invoice_date: toDateOnly(receivable.invoice_date),
      invoice_number: receivable.invoice_number || '',
      nf_number: receivable.nf_number || '',
      due_date: toDateOnly(receivable.due_date),
      forecast_date: toDateOnly(receivable.forecast_date),
      gross_amount: String(receivable.gross_amount || receivable.amount || ''),
      category_id: receivable.category_id || '',
      bank_account_id: receivable.bank_account_id || '',
      cost_center_id: receivable.cost_center_id || '',
      notes: receivable.notes || '',
      iss_enabled: !!issTax,
      iss_retention_type: issTax?.retention_type || 'not_retained',
      iss_municipality: issTax?.municipality || '',
      iss_base_amount: issTax?.base_amount ? String(issTax.base_amount) : '',
      iss_rate: issTax?.rate ? String(issTax.rate) : '',
      iss_retained_amount: issTax?.retained_amount ? String(issTax.retained_amount) : '',
      iss_receipt_number: issTax?.receipt_number || ''
    });
    setIsEditOpen(true);
  };

  // Function to open the batch edit modal for installments (edit multiple at once)
  const openBatchInstallmentEdit = (receivable) => {
    setSelectedReceivable(receivable);
    setInstallmentEditData({
      scope: 'single',
      description: '',
      customer_name: '',
      category_id: '',
      cost_center_id: '',
      amount: '',
      notes: ''
    });
    setIsInstallmentEditOpen(true);
  };

  const handleInstallmentEdit = async () => {
    if (!selectedReceivable?.installment_group_id) return;
    
    setIsSubmitting(true);
    try {
      const payload = { ...installmentEditData, receivable_id: selectedReceivable.id };
      
      // Clean empty values
      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null) {
          delete payload[key];
        }
      });
      
      // Convert amount to number if present
      if (payload.amount) {
        payload.amount = parseFloat(payload.amount);
      }
      
      await axios.patch(`${API_URL}/api/finance/receivable-installments/${selectedReceivable.installment_group_id}`, payload);
      
      toast.success('Parcelas atualizadas com sucesso!');
      setIsInstallmentEditOpen(false);
      fetchReceivables({ month: filterMonth, month_range: filterMonthRange });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar parcelas');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Convert to installments (venda parcelada)
  const openConvertToInstallmentsModal = (receivable) => {
    setSelectedReceivable(receivable);
    setConvertInstallmentsData({
      num_installments: '2',
      first_due_date: receivable.due_date || new Date().toISOString().split('T')[0]
    });
    setIsConvertToInstallmentsOpen(true);
  };

  const handleConvertToInstallments = async () => {
    if (!selectedReceivable) return;
    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/finance/receivables/${selectedReceivable.id}/convert-to-installments`,
        {
          num_installments: parseInt(convertInstallmentsData.num_installments),
          first_due_date: convertInstallmentsData.first_due_date
        },
        { headers }
      );
      toast.success(`Conta convertida em ${convertInstallmentsData.num_installments} parcelas!`);
      setIsConvertToInstallmentsOpen(false);
      setSelectedReceivable(null);
      fetchReceivables({ month: filterMonth, month_range: filterMonthRange });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao converter em parcelas');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReceiveModal = (receivable) => {
    setSelectedReceivable(receivable);
    setPaymentData({
      payment_date: new Date().toISOString().split('T')[0],
      received_amount: String(receivable.net_expected_amount || receivable.amount || ''),
      bank_account_id: '',
      discount: '0',
      interest_penalty: '0',
      notes: ''
    });
    setIsReceiveOpen(true);
  };

  const openViewModal = (receivable) => {
    setSelectedReceivable(receivable);
    setIsViewOpen(true);
  };

  const openDeleteDialog = (receivable) => {
    setSelectedReceivable(receivable);
    setIsDeleteOpen(true);
  };

  // Estornar (reverter) recebimento
  const [isReverseOpen, setIsReverseOpen] = useState(false);
  
  const handleReverse = async () => {
    if (!selectedReceivable) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/finance/receivables/${selectedReceivable.id}/reverse`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Recebimento estornado! A conta voltou ao status Pendente.');
      setIsReverseOpen(false);
      setSelectedReceivable(null);
      fetchReceivables({ month: filterMonth, ...(filterStatus !== 'all' ? { status: filterStatus } : {}) });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao estornar recebimento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedReceivable) return;
    setIsSubmitting(true);
    try {
      const deletedId = selectedReceivable.id;
      await axios.delete(`${API_URL}/api/finance/receivables/${deletedId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Conta excluída com sucesso!');
      setIsDeleteOpen(false);
      setSelectedReceivable(null);
      // Remoção otimista (evita o flash de "todas as filtradas sumiram")
      const removeReceivableLocal = useFinanceStore.getState().removeReceivableLocal;
      if (typeof removeReceivableLocal === 'function') removeReceivableLocal(deletedId);
      // Sincroniza com servidor em background (silencioso, sem skeleton)
      setTimeout(() => fetchReceivables(
        { month: filterMonth, ...(filterStatus !== 'all' ? { status: filterStatus } : {}) },
        { silent: true }
      ), 150);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir conta');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReplicateDialog = (receivable) => {
    setSelectedReceivable(receivable);
    // Default to next month's due date - use parseLocalDate to avoid timezone issues
    const originalDate = parseLocalDate(receivable.due_date);
    originalDate.setMonth(originalDate.getMonth() + 1);
    setReplicateDate(originalDate.toISOString().split('T')[0]);
    setIsReplicateOpen(true);
  };

  const handleReplicate = async () => {
    if (!selectedReceivable || !replicateDate) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/finance/receivables/${selectedReceivable.id}/replicate`, 
        { new_due_date: replicateDate }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Conta replicada com sucesso!');
      setIsReplicateOpen(false);
      setSelectedReceivable(null);
      setReplicateDate('');
      fetchReceivables({ month: filterMonth, ...(filterStatus !== 'all' ? { status: filterStatus } : {}) });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao replicar conta');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateCustomer = async () => {
    try {
      const created = await createCustomer({
        name: newCustomer.name,
        trade_name: newCustomer.trade_name || null,
        document: newCustomer.document || null,
        email: newCustomer.email || null,
        phone: newCustomer.phone || null,
        cep: newCustomer.cep || null,
        address: newCustomer.address || null,
        address_number: newCustomer.address_number || null,
        address_complement: newCustomer.address_complement || null,
        neighborhood: newCustomer.neighborhood || null,
        city: newCustomer.city || null,
        state: newCustomer.state || null,
        iss_retention_default: newCustomer.iss_retention_default,
        iss_municipality: newCustomer.iss_municipality || null,
        iss_default_rate: newCustomer.iss_default_rate ? parseFloat(newCustomer.iss_default_rate) : null
      });
      toast.success('Cliente criado!');
      setIsCustomerModalOpen(false);
      handleCustomerChange(created.id);
      setNewCustomer({ 
        name: '', trade_name: '', document: '', email: '', phone: '', 
        cep: '', address: '', address_number: '', address_complement: '',
        neighborhood: '', city: '', state: '',
        iss_retention_default: false, iss_municipality: '', iss_default_rate: '' 
      });
    } catch (error) {
      toast.error('Erro ao criar cliente');
    }
  };

  // Multi-select handlers
  const toggleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredReceivables.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredReceivables.map(r => r.id));
    }
  };

  const getSelectedTotal = () => {
    return receivables
      .filter(r => selectedItems.includes(r.id))
      .reduce((sum, r) => sum + (r.net_expected_amount || r.gross_amount || 0), 0);
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/finance/receivables/bulk`, {
        ids: selectedItems,
        action: 'delete'
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`${selectedItems.length} recebível(is) excluído(s) com sucesso!`);
      setIsBulkDeleteOpen(false);
      setSelectedItems([]);
      fetchReceivables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir recebíveis selecionados');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkEdit = async () => {
    if (selectedItems.length === 0) return;
    setIsSubmitting(true);
    try {
      const updateData = {};
      if (bulkEditData.category_id) updateData.category_id = bulkEditData.category_id;
      if (bulkEditData.forecast_date) updateData.forecast_date = bulkEditData.forecast_date;
      if (bulkEditData.competence_date) updateData.competence_date = bulkEditData.competence_date;
      if (bulkEditData.notes) updateData.notes = bulkEditData.notes;

      if (Object.keys(updateData).length === 0) {
        toast.error('Selecione pelo menos um campo para atualizar');
        return;
      }

      await axios.post(`${API_URL}/api/finance/receivables/bulk`, {
        ids: selectedItems,
        action: 'update',
        update_data: updateData
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`${selectedItems.length} recebível(is) atualizado(s) com sucesso!`);
      setIsBulkEditOpen(false);
      setSelectedItems([]);
      setBulkEditData({ category_id: '', forecast_date: '', competence_date: '', notes: '' });
      fetchReceivables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar recebíveis selecionados');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Helper function to check if item matches search term
  const matchesSearchReceivable = (r, term) => {
    const lowerTerm = term.toLowerCase();
    return (
      r.description?.toLowerCase().includes(lowerTerm) ||
      r.customer_name?.toLowerCase().includes(lowerTerm) ||
      r.category?.toLowerCase().includes(lowerTerm) ||
      r.cost_center?.toLowerCase().includes(lowerTerm) ||
      r.operation_nature?.toLowerCase().includes(lowerTerm) ||
      r.invoice_number?.toLowerCase().includes(lowerTerm) ||
      r.notes?.toLowerCase().includes(lowerTerm) ||
      r.bank_account_name?.toLowerCase().includes(lowerTerm)
    );
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm
    || Object.keys(advancedFilters).some(k => advancedFilters[k]?.value)
    || filterStatus !== 'all'
    || customerFilter !== 'all'
    || categoryFilter !== 'all'
    || costCenterFilter !== 'all'
    || !!quickPreset;

  // Sort function with useCallback for proper memoization
  const sortReceivables = useCallback((items) => {
    return [...items].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle category - it's a string field
      if (sortConfig.key === 'category') {
        aVal = a.category || '';
        bVal = b.category || '';
      }
      
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // Handle numbers
      if (sortConfig.key === 'amount' || sortConfig.key === 'net_expected_amount' || sortConfig.key === 'received_amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      // Handle dates - convert to comparable format
      if (sortConfig.key.includes('date') && aVal && bVal) {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      }
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortConfig]);

  const filteredReceivables = useMemo(() => {
    let result = [...receivables];

    // Hide tiny rendimentos bancários (REND PAGO APLIC < R$ 5)
    if (hideTinyRendimentos) {
      result = result.filter(r => !isTinyRendimento(r));
    }

    // Apply search term filter
    if (searchTerm) {
      result = result.filter(r => matchesSearchReceivable(r, searchTerm));
    }

    // Apply advanced filters
    if (Object.keys(advancedFilters).some(k => advancedFilters[k]?.value)) {
      result = applyAdvancedFilters(result, advancedFilters, dateColumns);
    }

    // Apply quick filters (customer, category, cost_center) + quickPreset client-side
    if (customerFilter !== 'all' || categoryFilter !== 'all' || costCenterFilter !== 'all' || quickPreset) {
      result = applyLedgerClientFilters(result, {
        customerFilter, categoryFilter, costCenterFilter, quickPreset,
        isOverdueFn: isOverdue,
      });
    }

    // Apply sorting
    return sortReceivables(result);
  }, [receivables, searchTerm, advancedFilters, sortReceivables, customerFilter, categoryFilter, costCenterFilter, quickPreset, hideTinyRendimentos]);

  // Calculate totals based on whether filter is active
  const dataSource = hasActiveFilters ? filteredReceivables : receivables;
  const pendingItems = dataSource.filter(r => r.status === 'pending');
  const receivedItems = dataSource.filter(r => r.status === 'received' || r.status === 'partially_received');
  const overdueItems = dataSource.filter(r => r.status === 'pending' && isOverdue(r.due_date));
  
  // Helpers para evitar dupla contagem em parc.recebido (mesmo legado com status=received):
  //  - openBalance(r): saldo aberto = max(0, esperado - recebido)
  //  - isEffectivelyPartial(r): detecta parcial real (status=partially_received OU residual
  //    vinculado OU recebido < esperado com recebido > 0). Usado pra evitar dobrar contagem
  //    quando o título original ficou marcado como "Recebido" mas só recebeu parte.
  //  - effectiveAmount(r): pra "Total Geral" — em parc.recebido usa o RECEBIDO (o saldo
  //    aparece como linha separada "Saldo devedor" que entra no Total como overdue).
  const _expected = (r) => parseFloat(r.net_expected_amount) || parseFloat(r.gross_amount) || parseFloat(r.amount) || 0;
  const _received = (r) => parseFloat(r.received_amount) || 0;
  const _openBalance = (r) => Math.max(0, _expected(r) - _received(r));
  const _isEffectivelyPartial = (r) => {
    if (r.status === 'partially_received') return true;
    if (r.status === 'received') {
      // Marcado como Recebido, mas tem saldo devedor vinculado OU recebeu menos que o esperado
      if (r.remaining_receivable_id || r.is_partial_payment || r.is_remaining_balance_child) return true;
      const exp = _expected(r);
      const rec = _received(r);
      if (rec > 0 && exp - rec > 0.01) return true;
    }
    return false;
  };
  const _effectiveAmount = (r) => _isEffectivelyPartial(r) ? _received(r) : _expected(r);

  const pendingTotal = pendingItems.reduce((sum, r) => sum + _openBalance(r), 0);
  const receivedTotal = receivedItems.reduce((sum, r) => sum + _received(r), 0);
  const overdueTotal = overdueItems.reduce((sum, r) => sum + _openBalance(r), 0);
  const totalGeral = dataSource.reduce((sum, r) => sum + _effectiveAmount(r), 0);

  // Render form fields (shared between create and edit)
  const renderFormFields = () => (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="basic">Dados Básicos</TabsTrigger>
        <TabsTrigger value="taxes" disabled={formData.operation_nature !== 'service'}>
          ISS {formData.iss_enabled && formData.operation_nature === 'service' && <Badge variant="secondary" className="ml-1 text-xs">Ativo</Badge>}
        </TabsTrigger>
        <TabsTrigger value="details">Detalhes</TabsTrigger>
      </TabsList>
      
      <TabsContent value="basic" className="space-y-4 mt-4">
        {/* Customer */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Cliente *</Label>
            <div className="flex gap-2">
              <Select value={formData.customer_id} onValueChange={handleCustomerChange}>
                <SelectTrigger className="flex-1" data-testid="select-customer">
                  <SelectValue placeholder="Selecione o cliente..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.filter(c => c.id).map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.iss_retention_default && <Badge variant="outline" className="ml-1 text-xs">ISS</Badge>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="icon" onClick={() => setIsCustomerModalOpen(true)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Natureza da Operação *</Label>
            <Select value={formData.operation_nature} onValueChange={(v) => setFormData({...formData, operation_nature: v})}>
              <SelectTrigger data-testid="select-nature">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPERATION_NATURES.map(n => (
                  <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label>Descrição / Serviço *</Label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            required
            data-testid="input-description"
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Competência</Label>
            <Input
              type="date"
              value={formData.competence_date}
              onChange={(e) => setFormData({...formData, competence_date: e.target.value})}
              data-testid="input-competence"
            />
          </div>
          <div className="space-y-2">
            <Label>Data da Nota</Label>
            <Input
              type="date"
              value={formData.invoice_date}
              onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
              data-testid="input-invoice-date"
            />
          </div>
          <div className="space-y-2">
            <Label>Nº do Documento</Label>
            <Input
              type="text"
              placeholder="Ex: 12345"
              value={formData.invoice_number}
              onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
              data-testid="input-invoice-number"
            />
          </div>
          <div className="space-y-2">
            <Label>Nº da Nota Fiscal</Label>
            <Input
              type="text"
              placeholder="Ex: 001234"
              value={formData.nf_number}
              onChange={(e) => setFormData({...formData, nf_number: e.target.value})}
              data-testid="input-nf-number"
            />
          </div>
          <div className="space-y-2">
            <Label>Vencimento *</Label>
            <Input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({...formData, due_date: e.target.value})}
              required
              data-testid="input-due-date"
            />
          </div>
          <div className="space-y-2">
            <Label>Previsão Receb.</Label>
            <Input
              type="date"
              value={formData.forecast_date}
              onChange={(e) => setFormData({...formData, forecast_date: e.target.value})}
              data-testid="input-forecast-date"
              title="Data prevista para recebimento (usada no fluxo de caixa)"
            />
          </div>
        </div>

        {/* Amount */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Valor Bruto (R$) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.gross_amount}
              onChange={(e) => setFormData({...formData, gross_amount: e.target.value})}
              required
              data-testid="input-amount"
            />
          </div>
          <div className="space-y-2">
            <Label>Categoria</Label>
            <div className="flex gap-2">
              <Select value={formData.category_id} onValueChange={(v) => setFormData({...formData, category_id: v})}>
                <SelectTrigger data-testid="select-category" className="flex-1">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.type === 'income' && c.id).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="icon" onClick={() => setIsCategoryModalOpen(true)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Centro de Custo */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Centro de Custo *</Label>
            <div className="flex gap-2">
              <Select value={formData.cost_center_id} onValueChange={(v) => setFormData({...formData, cost_center_id: v})}>
                <SelectTrigger className="flex-1" data-testid="select-cost-center">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {costCenters.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.code ? `${c.code} - ` : ''}{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="icon" onClick={() => setIsCreateCostCenterOpen(true)} title="Criar centro de custo">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Data de Competência</Label>
            <Input
              type="date"
              value={formData.competence_date}
              onChange={(e) => setFormData({...formData, competence_date: e.target.value})}
              data-testid="input-competence-date"
            />
            <p className="text-xs text-muted-foreground">Opcional. Preencha quando souber a data real.</p>
          </div>
        </div>

        {/* Bank Account - DESTAQUE para maior visibilidade */}
        <div className="space-y-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
          <Label className="font-medium">Conta Bancária (para recebimento)</Label>
          <Select value={formData.bank_account_id || "none"} onValueChange={(v) => setFormData({...formData, bank_account_id: v === "none" ? "" : v})}>
            <SelectTrigger data-testid="select-bank-account">
              <SelectValue placeholder="Selecione a conta..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhuma</SelectItem>
              {bankAccounts.filter(b => b.id).map(b => (
                <SelectItem key={b.id} value={b.id}>{b.name} - {b.bank_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Installment Option */}
        <Card className="border-dashed">
          <CardContent className="pt-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="is_installment"
                checked={formData.is_installment}
                onChange={(e) => setFormData({...formData, is_installment: e.target.checked})}
                className="h-4 w-4 rounded border-gray-300"
              />
              <div className="flex-1">
                <label htmlFor="is_installment" className="font-medium cursor-pointer">
                  Venda Parcelada
                </label>
                <p className="text-xs text-muted-foreground">
                  Divide o valor total em múltiplas parcelas com vencimentos mensais
                </p>
              </div>
            </div>
            
            {formData.is_installment && (
              <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Número de Parcelas</Label>
                  <Select value={formData.num_installments} onValueChange={(v) => setFormData({...formData, num_installments: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2,3,4,5,6,7,8,9,10,11,12,18,24,36,48,60,72,84,96,120,144,180,240,300].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Valor por Parcela</Label>
                  <div className="h-10 px-3 py-2 border rounded-md bg-muted text-sm font-mono">
                    {formatCurrency((parseFloat(formData.gross_amount) || 0) / (parseInt(formData.num_installments) || 1))}
                  </div>
                </div>
                <div className="col-span-2 text-sm text-muted-foreground bg-blue-50 p-3 rounded-lg">
                  <strong>Resumo:</strong> {formData.num_installments || 2} parcelas de{' '}
                  <span className="font-mono font-medium">
                    {formatCurrency((parseFloat(formData.gross_amount) || 0) / (parseInt(formData.num_installments) || 1))}
                  </span>
                  , com primeira parcela vencendo em <span className="font-medium">{formData.due_date ? formatDate(formData.due_date) : '(selecione a data)'}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Budget Number */}
        <div className="space-y-2">
          <Label>Nº do Orçamento</Label>
          <Input
            value={formData.budget_number || ''}
            onChange={(e) => setFormData({...formData, budget_number: e.target.value})}
            placeholder="Ex: 17870"
            data-testid="input-budget-number"
          />
        </div>

        {/* Net expected preview */}
        {formData.iss_enabled && formData.operation_nature === 'service' && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-700">Valor Líquido Esperado</span>
                <span className="text-lg font-bold font-mono text-blue-700">
                  {formatCurrency(netExpectedAmount)}
                </span>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                = Bruto ({formatCurrency(parseFloat(formData.gross_amount) || 0)}) - ISS Retido ({formatCurrency(parseFloat(formData.iss_retained_amount) || 0)})
              </p>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="taxes" className="space-y-4 mt-4">
        {formData.operation_nature !== 'service' ? (
          <Card className="border-dashed">
            <CardContent className="pt-6 text-center text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>ISS aplica-se apenas a operações de <strong>Serviço</strong></p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* ISS Enable Toggle */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-0.5">
                <Label className="text-base">ISS com retenção</Label>
                <p className="text-sm text-muted-foreground">
                  Habilitar cálculo de ISS retido na fonte
                </p>
              </div>
              <Switch
                checked={formData.iss_enabled}
                onCheckedChange={(checked) => setFormData({...formData, iss_enabled: checked})}
                data-testid="switch-iss"
              />
            </div>

            {formData.iss_enabled && (
              <>
                <Separator />
                
                {/* ISS Retention Type */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Percent className="h-4 w-4" /> Tipo de Retenção
                  </Label>
                  <Select 
                    value={formData.iss_retention_type} 
                    onValueChange={(v) => setFormData({...formData, iss_retention_type: v})}
                  >
                    <SelectTrigger data-testid="select-iss-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ISS_RETENTION_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.iss_retention_type !== 'not_retained' && (
                  <>
                    {/* Municipality */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" /> Município do ISS
                      </Label>
                      <Input
                        value={formData.iss_municipality}
                        onChange={(e) => setFormData({...formData, iss_municipality: e.target.value})}
                        placeholder="Ex: São Paulo/SP"
                        data-testid="input-iss-municipality"
                      />
                    </div>

                    {/* Base and Rate */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Base de Cálculo (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.iss_base_amount}
                          onChange={(e) => setFormData({...formData, iss_base_amount: e.target.value})}
                          placeholder="Normalmente = valor bruto"
                          data-testid="input-iss-base"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Alíquota (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={formData.iss_rate}
                          onChange={(e) => setFormData({...formData, iss_rate: e.target.value})}
                          placeholder="Ex: 5.00"
                          data-testid="input-iss-rate"
                        />
                      </div>
                    </div>

                    {/* Retained Amount */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Calculator className="h-4 w-4" /> Valor ISS Retido (R$)
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.iss_retained_amount}
                          onChange={(e) => setFormData({...formData, iss_retained_amount: e.target.value})}
                          className={formData.iss_retention_type === 'fully_retained' ? 'bg-muted' : ''}
                          readOnly={formData.iss_retention_type === 'fully_retained'}
                          data-testid="input-iss-retained"
                        />
                        {formData.iss_retention_type === 'fully_retained' && (
                          <p className="text-xs text-muted-foreground">Calculado automaticamente</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Comprovante de Retenção</Label>
                        <Input
                          value={formData.iss_receipt_number}
                          onChange={(e) => setFormData({...formData, iss_receipt_number: e.target.value})}
                          placeholder="Nº do comprovante"
                          data-testid="input-iss-receipt"
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </TabsContent>

      <TabsContent value="details" className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label>Observações</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            rows={4}
            placeholder="Informações adicionais sobre este recebível..."
          />
        </div>
      </TabsContent>
    </Tabs>
  );

  // Build period label + handler for PDF export
  const buildPeriodLabel = () => {
    if (filterMonthRange === -1) {
      const start = customStartDate ? customStartDate.split('-').reverse().join('/') : '?';
      const end = customEndDate ? customEndDate.split('-').reverse().join('/') : '?';
      return `${start} a ${end}`;
    } else if (filterMonthRange === 0) {
      return 'Todos os períodos';
    } else if (filterMonthRange === 1) {
      return formatMonth(filterMonth);
    } else {
      const [y, m] = filterMonth.split('-').map(n => parseInt(n));
      const startDate = new Date(y, m - 1, 1);
      const endDate = new Date(y, m - 1 + filterMonthRange - 1, 1);
      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      if (startDate.getFullYear() === endDate.getFullYear()) {
        return `${months[startDate.getMonth()]} a ${months[endDate.getMonth()]}/${endDate.getFullYear()}`;
      } else {
        return `${months[startDate.getMonth()]}/${startDate.getFullYear()} a ${months[endDate.getMonth()]}/${endDate.getFullYear()}`;
      }
    }
  };

  const doExportPdf = () => {
    if (!dataSource || dataSource.length === 0) {
      toast.warning('Nenhum lançamento para gerar PDF.');
      return;
    }
    const advancedDesc = Object.entries(advancedFilters || {})
      .filter(([, v]) => v?.value)
      .map(([k, v]) => `${k}: ${v.value}`)
      .join('; ');
    const summary = {
      pending: { total: pendingTotal, count: pendingItems.length },
      paid: { total: receivedTotal, count: receivedItems.length },
      overdue: { total: overdueTotal, count: overdueItems.length },
      total: totalGeral,
      count: dataSource.length,
    };
    try {
      const selectedAccount = (!allSelected && Array.isArray(selectedAccountIds) && selectedAccountIds.length === 1)
        ? (bankAccounts || []).find(a => a.id === selectedAccountIds[0] || a._id === selectedAccountIds[0])
        : null;
      generateLedgerPdf({
        type: 'receivables',
        items: dataSource,
        summary,
        filters: {
          period_label: buildPeriodLabel(),
          status: filterStatus,
          date_field: dateFilterField,
          search: searchTerm,
          advanced: advancedDesc,
        },
        tenant,
        user,
        selectedAccount,
      });
      toast.success('PDF gerado com sucesso');
    } catch (e) {
      toast.error('Erro ao gerar PDF');
    }
  };

  return (
    <div className="space-y-4" data-testid="receivables-page">
      <ValidateExportDialog
        open={exportValidatorOpen}
        onOpenChange={setExportValidatorOpen}
        kind="receivable"
        startDate={filterMonthRange === -1 ? customStartDate : null}
        endDate={filterMonthRange === -1 ? customEndDate : null}
        dateField={dateFilterField}
        onExportAnyway={doExportPdf}
      />
      {/* Summary Cards CLICÁVEIS */}
      <ClickableSummaryCards
        summary={{
          pending: { total: pendingTotal, count: pendingItems.length },
          paid: { total: receivedTotal, count: receivedItems.length },
          overdue: { total: overdueTotal, count: overdueItems.length },
          total: totalGeral,
          count: dataSource.length,
        }}
        statusActive={filterStatus}
        onStatusChange={(target) => {
          if (target === '__overdue__') {
            setQuickPreset(quickPreset === 'overdue' ? null : 'overdue');
            setFilterStatus('unpaid');
            setFilterMonthRange(0);
          } else {
            setQuickPreset(null);
            setFilterStatus(target);
          }
        }}
        variant="receivables"
        hasActiveFilters={hasActiveFilters}
      />

      {/* Atalhos rápidos (presets) */}
      <QuickPresets
        preset={quickPreset}
        onApply={(p) => {
          const today = new Date();
          const ym = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
          setQuickPreset(p);
          if (p === 'overdue' || p === 'all_open' || p === 'overdue_today') {
            setFilterStatus('unpaid');
            setFilterMonthRange(0);
            setDateFilterField('due_date');
          } else if (p === 'today' || p === 'this_week') {
            setFilterStatus('unpaid');
            setFilterMonth(ym);
            setFilterMonthRange(1);
            setDateFilterField('due_date');
          } else if (p === 'received_this_month') {
            setFilterStatus('received');
            setFilterMonth(ym);
            setFilterMonthRange(1);
            setDateFilterField('received_date');
          } else if (p === null) {
            setFilterStatus('all');
          }
        }}
        variant="receivables"
      />

      {/* ===== TOOLBAR REORGANIZADA ===== */}
      <div className="space-y-2">
        {/* Linha 1: Filtros + Ações */}
        <div className="flex flex-wrap gap-2 items-center">
          <MonthNavigator
            filterMonth={filterMonth}
            setFilterMonth={setFilterMonth}
            filterMonthRange={filterMonthRange}
            setFilterMonthRange={setFilterMonthRange}
            formatMonth={formatMonth}
            changeMonth={changeMonth}
            dateFilterField={dateFilterField}
            setDateFilterField={setDateFilterField}
            dateFieldOptions={dateFieldOptions}
            customStartDate={customStartDate}
            setCustomStartDate={setCustomStartDate}
            customEndDate={customEndDate}
            setCustomEndDate={setCustomEndDate}
          />

          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar por descrição, cliente, NF..."
            testId="search-receivables"
          />

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="filter-status">
              <Filter className="h-3.5 w-3.5 mr-1.5" /><SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              <SelectItem value="unpaid">Em aberto</SelectItem>
              <SelectItem value="pending">Pendente</SelectItem>
              <SelectItem value="received">Recebido</SelectItem>
              <SelectItem value="partially_received">Parc. Receb.</SelectItem>
              <SelectItem value="cancelled">Cancelado</SelectItem>
            </SelectContent>
          </Select>

          <AdvancedFilters
            columns={filterableColumns}
            filters={advancedFilters}
            onFilterChange={setAdvancedFilters}
            onClearFilters={() => setAdvancedFilters({})}
            dateColumns={dateColumns}
          />

          {/* Toggle rendimentos bancários */}
          <Button
            variant={hideTinyRendimentos ? 'outline' : 'default'}
            size="sm"
            onClick={() => setHideTinyRendimentos(!hideTinyRendimentos)}
            data-testid="toggle-rendimentos"
            className="h-9 text-xs"
            title={hideTinyRendimentos ? 'Rendimentos bancários (REND PAGO APLIC) < R$ 5 estão ocultos. Clique para mostrar.' : 'Clique para ocultar rendimentos bancários minúsculos.'}
          >
            {hideTinyRendimentos ? '🙈 Sem rend.' : '👁 Com rend.'}
          </Button>

          <div className="ml-auto flex gap-2 items-center">
            <ColumnSelector
              columns={RECEIVABLE_COLUMNS}
              visibleColumns={visibleColumns}
              onChange={setVisibleColumns}
              storageKey="receivables_visible_columns"
            />
            <ExportDropdown
              onExportExcel={() => exportToExcel(filteredReceivables, EXPORT_CONFIGS.receivables, `contas-a-receber-${filterMonth}`, 'Contas a Receber')}
              onExportPdf={() => setExportValidatorOpen(true)}
            />

            {user?.role === 'admin' && (
              <AdminToolsDropdown
                items={[
                  {
                    label: 'Restaurar Valores Originais',
                    hint: 'Recupera Bruto/Líquido sobrescritos',
                    icon: <RotateCcw className="h-4 w-4 text-amber-600" />,
                    testId: 'restore-original-amounts-btn',
                    onClick: async () => {
                      if (!window.confirm('Restaurar valores Bruto e Liquido originais em recebiveis que tiveram baixa parcial antes do fix? Isso corrige NFs com valores sobrescritos.')) return;
                      try {
                        const res = await axios.post(`${API_URL}/api/finance/receivables/restore-original-amounts`, {}, { headers: { Authorization: `Bearer ${useAuthStore.getState().token}` } });
                        toast.success(res.data.message || 'Valores restaurados');
                        window.location.reload();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Erro ao restaurar');
                      }
                    },
                  },
                  {
                    label: 'Redistribuir Valores Compartilhados',
                    hint: 'Corrige duplicações de OFX',
                    icon: <Layers className="h-4 w-4 text-blue-700" />,
                    testId: 'redistribute-multi-btn',
                    onClick: async () => {
                      if (!window.confirm('Quando 1 transacao bancaria foi conciliada com varios titulos, o valor recebido pode ter sido duplicado em todos. Esse botao redistribui proporcionalmente. Continuar?')) return;
                      try {
                        const res = await axios.post(`${API_URL}/api/finance/receivables/redistribute-multi-linked`, {}, { headers: { Authorization: `Bearer ${useAuthStore.getState().token}` } });
                        toast.success(res.data.message || 'Redistribuicao concluida');
                        window.location.reload();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Erro');
                      }
                    },
                  },
                ]}
              />
            )}

            <Dialog open={isCreateOpen} onOpenChange={handleCreateDialogChange}>
              <DialogTrigger asChild>
                <Button className="h-9 bg-[#001859] hover:bg-[#001859]/90" data-testid="new-receivable-btn">
                  <Plus className="h-4 w-4 mr-1.5" />Nova Conta
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nova Conta a Receber</DialogTitle>
                  <DialogDescription>Cadastre um novo título a receber com suporte a ISS</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate}>
                  {renderFormFields()}
                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => handleCreateDialogChange(false)}>Cancelar</Button>
                  <Button type="submit" disabled={isSubmitting} data-testid="submit-receivable">
                    {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Salvar
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>{/* /ml-auto ações */}
        </div>{/* /linha 1 filtros */}

        {/* Linha 2: Filtros rápidos + Agrupamento */}
        <div className="flex flex-wrap gap-2 items-center">
          <QuickFilters
            variant="receivables"
            customers={customers}
            categories={categories}
            costCenters={costCenters}
            customerFilter={customerFilter}
            setCustomerFilter={setCustomerFilter}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            costCenterFilter={costCenterFilter}
            setCostCenterFilter={setCostCenterFilter}
          />
          <div className="ml-auto">
            <GroupByDropdown value={groupBy} onChange={setGroupBy} variant="receivables" />
          </div>
        </div>
      </div>{/* /toolbar wrapper */}

      {/* ===== Chips de filtros ativos ===== */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <ActiveFilterChips
            chips={[
              ...buildFilterChips({
                searchTerm,
                clearSearch: () => setSearchTerm(''),
                filterStatus,
                clearStatus: () => setFilterStatus('all'),
                statusLabels: { unpaid: 'Em aberto', pending: 'Pendente', received: 'Recebido', partially_received: 'Parc. Receb.', cancelled: 'Cancelado' },
                advancedFilters,
                setAdvancedFilters,
                filterableColumnsByKey: Object.fromEntries(filterableColumns.map(c => [c.key, c])),
              }),
              ...(customerFilter !== 'all' ? [{
                key: 'customer',
                label: 'Cliente',
                value: (customers.find(c => (c.id || c.name) === customerFilter)?.name || customerFilter).slice(0, 24),
                onRemove: () => setCustomerFilter('all'),
              }] : []),
              ...(categoryFilter !== 'all' ? [{
                key: 'category',
                label: 'Categoria',
                value: (categories.find(c => (c.id || c.name) === categoryFilter)?.name || categoryFilter).slice(0, 24),
                onRemove: () => setCategoryFilter('all'),
              }] : []),
              ...(costCenterFilter !== 'all' ? [{
                key: 'cost_center',
                label: 'CNPJ',
                value: (costCenters.find(c => c.id === costCenterFilter)?.name || '').slice(0, 24),
                onRemove: () => setCostCenterFilter('all'),
              }] : []),
              ...(quickPreset ? [{
                key: 'preset',
                label: 'Atalho',
                value: {
                  overdue_today: 'Vencidos + Hoje', overdue: 'Vencidos', today: 'Vence hoje', this_week: 'Esta semana',
                  all_open: 'Em aberto', received_this_month: 'Recebidos no mês',
                }[quickPreset] || quickPreset,
                onRemove: () => { setQuickPreset(null); setFilterStatus('all'); },
              }] : []),
            ]}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px] text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            onClick={() => {
              setSearchTerm('');
              setFilterStatus('all');
              setAdvancedFilters({});
              setCustomerFilter('all');
              setCategoryFilter('all');
              setCostCenterFilter('all');
              setQuickPreset(null);
            }}
            data-testid="clear-all-filters-btn"
          >
            <X className="h-3 w-3 mr-1" />Limpar tudo
          </Button>
        </div>
      )}

      {/* ===== Visão Agrupada ===== */}
      {groupBy !== 'none' && (() => {
        const grouped = groupLedgerItems(dataSource, groupBy, 'net_expected_amount');
        const grouping_label = ({ customer: 'Cliente', category: 'Categoria', cost_center: 'CNPJ/Centro', month: 'Mês' })[groupBy];
        if (!grouped || grouped.length === 0) {
          return (
            <Card className="border-dashed border-blue-300 bg-blue-50/30" data-testid="grouped-view">
              <CardContent className="p-4 text-center text-sm text-muted-foreground">
                <Layers className="h-5 w-5 mx-auto mb-1 text-blue-400" />
                Agrupamento por <strong className="text-[#001859]">{grouping_label}</strong> ativo, mas nenhum lançamento no filtro atual.
              </CardContent>
            </Card>
          );
        }
        const grandTotal = grouped.reduce((s, g) => s + g.total, 0);
        const grandCount = grouped.reduce((s, g) => s + g.count, 0);
        return (
          <Card className="border-blue-200" data-testid="grouped-view">
            <CardContent className="p-0">
              <div className="p-3 border-b bg-blue-50/40 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#001859]">
                    Agrupado por {grouping_label}
                  </span>
                  <Badge variant="secondary" className="text-xs">{grouped.length} grupo(s)</Badge>
                </div>
                <div className="text-sm font-mono font-bold text-[#001859]">{formatCurrency(grandTotal)} • {grandCount} título(s)</div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-xs md:text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Grupo</th>
                      <th className="px-3 py-2 text-center font-semibold">Qtd</th>
                      <th className="px-3 py-2 text-right font-semibold">Total</th>
                      <th className="px-3 py-2 text-right font-semibold">% do total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map(g => (
                      <tr key={g.key} className="border-b hover:bg-blue-50/30">
                        <td className="px-3 py-1.5 max-w-[360px] truncate font-medium" title={g.key}>{g.key}</td>
                        <td className="px-3 py-1.5 text-center text-xs text-slate-500">{g.count}</td>
                        <td className="px-3 py-1.5 text-right font-mono font-bold tabular-nums">{formatCurrency(g.total)}</td>
                        <td className="px-3 py-1.5 text-right text-xs text-blue-600 font-semibold">{grandTotal ? ((g.total / grandTotal) * 100).toFixed(1) : '0.0'}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Bulk Actions Bar */}
      {selectedItems.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">
              {selectedItems.length} item(ns) selecionado(s) • Total: {formatCurrency(getSelectedTotal())}
            </span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                // Filtra apenas selecionados que ainda não foram totalmente recebidos
                const nonReceived = receivables.filter(r => selectedItems.includes(r.id) && r.status !== 'received');
                if (nonReceived.length === 0) {
                  toast.error('Nenhum dos selecionados está em aberto. Filtre por "Em aberto" ou "Parc. Recebido".');
                  return;
                }
                if (nonReceived.length < selectedItems.length) {
                  toast.info(`${selectedItems.length - nonReceived.length} item(s) já recebido(s) foram ignorados.`);
                }
                setIsBulkReceiveOpen(true);
              }}
              data-testid="bulk-receive-btn"
              title="Registrar 1 PIX rateado em várias contas a receber"
            >
              <Layers className="h-4 w-4 mr-2" />
              Receber em Lote (Rateio)
            </Button>
            <Button variant="outline" size="sm" onClick={() => setIsBulkEditOpen(true)} data-testid="bulk-edit-btn">
              <Edit className="h-4 w-4 mr-2" />
              Editar em Lote
            </Button>
            {isAdmin && <Button variant="destructive" size="sm" onClick={() => setIsBulkDeleteOpen(true)} data-testid="bulk-delete-btn">
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir Selecionados
            </Button>}
            <Button variant="ghost" size="sm" onClick={() => setSelectedItems([])}>
              Limpar Seleção
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {receivablesLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (<Skeleton key={i} className="h-12 w-full" />))}
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isColumnVisible('obs') && <TableHead className="w-12 text-center hidden lg:table-cell">Obs</TableHead>}
                  {isColumnVisible('checkbox') && (
                    <TableHead className="w-10">
                      <Checkbox 
                        checked={selectedItems.length > 0 && selectedItems.length === filteredReceivables.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                  )}
                  {isColumnVisible('competence_date') && (
                    <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('competence_date')} title="Mês/Ano do serviço (vem da OS). Não confundir com data de vencimento ou emissão da NF.">
                      <div className="flex items-center gap-1">
                        Competência
                        {sortConfig.key === 'competence_date' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {/* ORDEM (2026-06-10): Competência > Descrição > Nº Doc > Nº NF > Categoria > Bruto >
                       Vencimento > Previsão > Recebimento > Cliente > Natureza > Conta > Líquido > Recebido > Status > Ações */}
                  {isColumnVisible('description') && (
                    <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('description')}>
                      <div className="flex items-center gap-1">
                        Descrição
                        {sortConfig.key === 'description' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('invoice_number') && (
                    <TableHead className="cursor-pointer hover:bg-muted/50 hidden lg:table-cell" onClick={() => handleSort('invoice_number')}>
                      <div className="flex items-center gap-1">
                        Nº Doc
                        {sortConfig.key === 'invoice_number' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('nf_number') && (
                    <TableHead className="cursor-pointer hover:bg-muted/50 hidden lg:table-cell" onClick={() => handleSort('nf_number')}>
                      <div className="flex items-center gap-1">
                        Nº NF
                        {sortConfig.key === 'nf_number' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('category') && (
                    <TableHead className="cursor-pointer hover:bg-muted/50 hidden md:table-cell" onClick={() => handleSort('category')}>
                      <div className="flex items-center gap-1">
                        Categoria
                        {sortConfig.key === 'category' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('gross_amount') && (
                    <TableHead className="text-right cursor-pointer hover:bg-muted/50" onClick={() => handleSort('amount')}>
                      <div className="flex items-center justify-end gap-1">
                        Bruto
                        {sortConfig.key === 'amount' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('due_date') && (
                    <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('due_date')}>
                      <div className="flex items-center gap-1">
                        Vencimento
                        {sortConfig.key === 'due_date' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('forecast_date') && (
                    <TableHead className="cursor-pointer hover:bg-muted/50 hidden md:table-cell" onClick={() => handleSort('forecast_date')}>
                      <div className="flex items-center gap-1">
                        Previsão
                        {sortConfig.key === 'forecast_date' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('payment_date') && (
                    <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('payment_date')}>
                      <div className="flex items-center gap-1">
                        Recebimento
                        {sortConfig.key === 'payment_date' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('customer_name') && (
                    <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('customer_name')}>
                      <div className="flex items-center gap-1">
                        Cliente
                        {sortConfig.key === 'customer_name' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('nature') && <TableHead className="hidden lg:table-cell">Natureza</TableHead>}
                  {isColumnVisible('bank_account') && <TableHead className="hidden lg:table-cell">Conta</TableHead>}
                  {isColumnVisible('iss_retained') && <TableHead className="text-right hidden lg:table-cell">ISS Ret.</TableHead>}
                  {isColumnVisible('net_amount') && (
                    <TableHead className="text-right cursor-pointer hover:bg-muted/50" onClick={() => handleSort('net_expected_amount')}>
                      <div className="flex items-center justify-end gap-1">
                        Líquido
                        {sortConfig.key === 'net_expected_amount' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('received_amount') && (
                    <TableHead className="text-right cursor-pointer hover:bg-muted/50" onClick={() => handleSort('received_amount')}>
                      <div className="flex items-center justify-end gap-1">
                        Recebido
                        {sortConfig.key === 'received_amount' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('status') && (
                    <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('status')}>
                      <div className="flex items-center gap-1">
                        Status
                        {sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </div>
                    </TableHead>
                  )}
                  {isColumnVisible('actions') && <TableHead className="text-right">Ações</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReceivables.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="text-center py-8 text-muted-foreground">
                      Nenhuma conta a receber encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReceivables.map((r) => {
                    const overdue = r.status === 'pending' && isOverdue(r.due_date);
                    const isReceived = r.status === 'received' || r.status === 'partially_received';
                    const effectivePartial = _isEffectivelyPartial(r);
                    const issTax = r.taxes?.find(t => t.tax_type === 'ISS');
                    const isSelected = selectedItems.includes(r.id);
                    const rowClass = isSelected
                      ? 'bg-primary/10'
                      : isReceived 
                        ? effectivePartial ? 'bg-amber-50 hover:bg-amber-100' : 'bg-green-50 hover:bg-green-100' 
                        : overdue 
                          ? 'bg-red-50 hover:bg-red-100' 
                          : 'hover:bg-muted/50';
                    return (
                      <TableRow key={r.id} className={rowClass} data-testid={`receivable-row-${r.id}`}>
                        {isColumnVisible('obs') && (
                          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            <NotesCell
                              value={localComments[r.id] !== undefined ? localComments[r.id] : (r.notes || '')}
                              onSave={(v) => { setLocalComments(prev => ({ ...prev, [r.id]: v })); saveComment(r.id, v); }}
                              testId={`notes-btn-${r.id}`}
                            />
                          </TableCell>
                        )}
                        {isColumnVisible('checkbox') && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox 
                              checked={isSelected}
                              onCheckedChange={() => toggleSelectItem(r.id)}
                            />
                          </TableCell>
                        )}
                        {isColumnVisible('competence_date') && (
                          <TableCell className="font-mono text-xs whitespace-nowrap">
                            {(() => {
                              const c = r.competence_date;
                              if (!c) return <span className="text-muted-foreground">—</span>;
                              const s = typeof c === 'string' ? c.substring(0, 10) : c;
                              const m = /^(\d{4})-(\d{2})/.exec(s);
                              return m ? <span className="font-medium">{m[2]}/{m[1]}</span> : formatDate(c);
                            })()}
                          </TableCell>
                        )}
                        {isColumnVisible('description') && (
                          <TableCell className="max-w-[150px]">
                            <div className="flex items-center gap-2">
                              <span className="truncate">{r.description}</span>
                              {r.installment_group_id && (
                                <Badge variant="secondary" className="text-xs shrink-0">
                                  {r.installment_number}/{r.total_installments}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        )}
                        {isColumnVisible('invoice_number') && (
                          <TableCell className="hidden lg:table-cell font-mono text-sm">
                            {r.invoice_number || '-'}
                          </TableCell>
                        )}
                        {isColumnVisible('nf_number') && (
                          <TableCell className="hidden lg:table-cell font-mono text-sm">
                            {r.nf_number || '-'}
                          </TableCell>
                        )}
                        {isColumnVisible('category') && (
                          <TableCell className="hidden md:table-cell">
                            {r.category || '-'}
                          </TableCell>
                        )}
                        {isColumnVisible('gross_amount') && (
                          <TableCell className={`text-right font-mono ${isReceived ? 'text-green-700' : overdue ? 'text-red-600' : ''}`}>
                            {formatCurrency(r.gross_amount || r.amount)}
                          </TableCell>
                        )}
                        {isColumnVisible('due_date') && (
                          <TableCell className={overdue ? 'text-red-600 font-medium' : isReceived ? 'text-green-700' : ''}>
                            {formatDate(r.due_date)}
                          </TableCell>
                        )}
                        {isColumnVisible('forecast_date') && (
                          <TableCell className="text-muted-foreground">
                            {r.forecast_date ? formatDate(r.forecast_date) : '-'}
                          </TableCell>
                        )}
                        {isColumnVisible('payment_date') && (
                          <TableCell className={isReceived ? 'text-green-700 font-medium' : 'text-muted-foreground'}>
                            {r.payment_date ? formatDate(r.payment_date) : '-'}
                          </TableCell>
                        )}
                        {isColumnVisible('customer_name') && (
                          <TableCell>{r.customer_name}</TableCell>
                        )}
                        {isColumnVisible('nature') && (
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {OPERATION_NATURES.find(n => n.value === r.operation_nature)?.label || r.operation_nature}
                            </Badge>
                          </TableCell>
                        )}
                        {isColumnVisible('bank_account') && (
                          <TableCell className="text-muted-foreground">
                            {r.bank_account_name || '-'}
                          </TableCell>
                        )}
                        {isColumnVisible('iss_retained') && (
                          <TableCell className="text-right font-mono text-red-600">
                            {issTax?.retained_amount ? `- ${formatCurrency(issTax.retained_amount)}` : '-'}
                          </TableCell>
                        )}
                        {isColumnVisible('net_amount') && (
                          <TableCell className={`text-right font-mono font-medium ${isReceived ? 'text-green-700' : overdue ? 'text-red-600' : 'text-green-600'}`}>
                            {effectivePartial ? (
                              <div className="leading-tight">
                                <div className="text-green-700 font-semibold">
                                  {formatCurrency(r.received_amount || 0)}
                                </div>
                                <div className="text-[10px] text-muted-foreground">
                                  de {formatCurrency(r.net_expected_amount || r.amount || 0)}
                                </div>
                                <div className="text-[10px] text-amber-700 font-medium">
                                  saldo {formatCurrency(Math.max(0, (parseFloat(r.net_expected_amount || r.amount) || 0) - (parseFloat(r.received_amount) || 0)))}
                                </div>
                              </div>
                            ) : (
                              formatCurrency(r.net_expected_amount || r.amount)
                            )}
                          </TableCell>
                        )}
                        {isColumnVisible('received_amount') && (
                          <TableCell className="text-right font-mono">
                            {r.received_amount ? (
                              <span className={`font-medium ${r.received_amount < (r.net_expected_amount || r.amount) ? 'text-amber-600' : 'text-green-700'}`}>
                                {formatCurrency(r.received_amount)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        )}
                        {isColumnVisible('status') && (
                          <TableCell>
                            <Badge className={getStatusBadgeClass(overdue ? 'overdue' : (effectivePartial ? 'partially_received' : r.status))}>
                              {getStatusLabel(overdue ? 'overdue' : (effectivePartial ? 'partially_received' : r.status))}
                            </Badge>
                          </TableCell>
                        )}
                        {isColumnVisible('actions') && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openViewModal(r)} title="Ver detalhes">
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openEditModal(r)} title="Editar">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openReplicateDialog(r)} title="Replicar">
                                <Copy className="h-4 w-4" />
                              </Button>
                              {r.status === 'pending' && !r.installment_group_id && (
                                <Button variant="ghost" size="icon" onClick={() => openConvertToInstallmentsModal(r)} title="Parcelar (Venda Parcelada)" className="text-blue-600 hover:text-blue-700">
                                  <Layers className="h-4 w-4" />
                                </Button>
                              )}
                              {(r.status === 'pending' || r.status === 'partially_received') && (
                                <Button variant="ghost" size="icon" onClick={() => openReceiveModal(r)} title="Registrar Recebimento">
                                  <DollarSign className="h-4 w-4 text-green-600" />
                                </Button>
                              )}
                              {(r.status === 'received' || r.status === 'partially_received') && (
                                <Button variant="ghost" size="icon" className="text-amber-600 hover:text-amber-700" onClick={() => { setSelectedReceivable(r); setIsReverseOpen(true); }} title="Estornar recebimento">
                                  <RotateCcw className="h-4 w-4" />
                                </Button>
                              )}
                              {r.is_remaining_balance && (
                                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Saldo</Badge>
                              )}
                              {isAdmin && <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" onClick={() => openDeleteDialog(r)} title="Excluir">
                                <Trash2 className="h-4 w-4" />
                              </Button>}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Convert to Installments Modal */}
      <Dialog open={isConvertToInstallmentsOpen} onOpenChange={setIsConvertToInstallmentsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Parcelar Conta a Receber</DialogTitle>
            <DialogDescription>
              Dividir esta conta em múltiplas parcelas (venda parcelada)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-medium text-blue-800">{selectedReceivable?.description}</p>
              <p className="text-lg font-bold text-blue-900 mt-1">
                {formatCurrency(selectedReceivable?.gross_amount || selectedReceivable?.amount || 0)}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Cliente: {selectedReceivable?.customer_name || '-'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Número de Parcelas</Label>
              <Select 
                value={convertInstallmentsData.num_installments} 
                onValueChange={(v) => setConvertInstallmentsData({...convertInstallmentsData, num_installments: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2,3,4,5,6,7,8,9,10,11,12,18,24,36,48,60].map(n => (
                    <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Vencimento da 1ª Parcela</Label>
              <Input 
                type="date" 
                value={convertInstallmentsData.first_due_date}
                onChange={(e) => setConvertInstallmentsData({...convertInstallmentsData, first_due_date: e.target.value})}
              />
            </div>
            
            <div className="p-3 bg-gray-50 border rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Valor por parcela:</strong>{' '}
                <span className="font-mono font-medium">
                  {formatCurrency((selectedReceivable?.gross_amount || selectedReceivable?.amount || 0) / (parseInt(convertInstallmentsData.num_installments) || 1))}
                </span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Serão criadas {convertInstallmentsData.num_installments} parcelas com vencimentos mensais a partir de {convertInstallmentsData.first_due_date ? formatDate(convertInstallmentsData.first_due_date) : '-'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConvertToInstallmentsOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConvertToInstallments} disabled={isSubmitting || !convertInstallmentsData.first_due_date}>
              {isSubmitting ? 'Parcelando...' : 'Confirmar Parcelamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Conta a Receber</DialogTitle>
            {(selectedReceivable?.status === 'received' || selectedReceivable?.status === 'partially_received') && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800">
                  {selectedReceivable?.status === 'partially_received' ? 'Conta parcialmente recebida' : 'Conta já recebida'}
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Você pode editar: descrição, cliente, categoria, natureza, centro de custo e observações.
                  <br />
                  <strong>Valores financeiros e datas não podem ser alterados.</strong> Use "Estornar" se necessário.
                </p>
                {selectedReceivable?.remaining_balance > 0 && (
                  <p className="text-xs text-amber-800 mt-1 font-semibold">
                    Saldo restante: {formatCurrency(selectedReceivable.remaining_balance)}
                  </p>
                )}
              </div>
            )}
          </DialogHeader>
          <form onSubmit={handleEdit}>
            {renderFormFields()}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar Alterações
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receive Payment Modal */}
      <Dialog open={isReceiveOpen} onOpenChange={(open) => {
        // Re-fetch contas bancárias toda vez que o dialog abre, garantindo
        // que contas recém-criadas em outra aba apareçam no dropdown.
        if (open) { try { fetchBankAccounts(); } catch {} }
        setIsReceiveOpen(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Recebimento</DialogTitle>
            <DialogDescription>
              {selectedReceivable?.description}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReceive} className="space-y-4">
            <Card className="bg-muted/50">
              <CardContent className="pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Valor Esperado:</span>
                  <span className="font-mono font-medium">{formatCurrency(selectedReceivable?.net_expected_amount || selectedReceivable?.amount || 0)}</span>
                </div>
                {selectedReceivable?.total_retained > 0 && (
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>ISS Retido:</span>
                    <span className="font-mono">- {formatCurrency(selectedReceivable.total_retained)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label>Data do Recebimento *</Label>
              <Input
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Conta Bancária *</Label>
              <Select value={paymentData.bank_account_id} onValueChange={(v) => setPaymentData({...paymentData, bank_account_id: v})}>
                <SelectTrigger className={!paymentData.bank_account_id ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Selecione a conta..." />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.filter(b => b.id).map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.bank_name} - {b.account_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!paymentData.bank_account_id && <p className="text-xs text-red-500">Obrigatório selecionar a conta bancária</p>}
            </div>

            <div className="space-y-2">
              <Label>Valor Recebido (R$) *</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentData.received_amount}
                onChange={(e) => setPaymentData({...paymentData, received_amount: e.target.value})}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Desconto (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentData.discount}
                  onChange={(e) => setPaymentData({...paymentData, discount: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Juros/Multa (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentData.interest_penalty}
                  onChange={(e) => setPaymentData({...paymentData, interest_penalty: e.target.value})}
                />
              </div>
            </div>

            <Card className={paymentDifference >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
              <CardContent className="pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Diferença:</span>
                  <span className={`font-mono font-bold ${paymentDifference >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {paymentDifference >= 0 ? '+' : ''}{formatCurrency(paymentDifference)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Partial Payment - Remaining Balance Section */}
            {remainingBalance > 0 && (
              <Card className="bg-amber-50 border-amber-300">
                <CardContent className="pt-4 space-y-4">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-medium text-sm">Pagamento Parcial Detectado</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-amber-700">Saldo Devedor:</span>
                    <span className="font-mono font-bold text-amber-900">{formatCurrency(remainingBalance)}</span>
                  </div>
                  <p className="text-xs text-amber-700">
                    Um novo lançamento será criado automaticamente para o saldo devedor.
                  </p>
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-amber-200">
                    <div className="space-y-1">
                      <Label className="text-xs text-amber-800">Vencimento do Saldo</Label>
                      <Input
                        type="date"
                        value={paymentData.remaining_balance_due_date}
                        onChange={(e) => setPaymentData({...paymentData, remaining_balance_due_date: e.target.value})}
                        className="h-8 text-sm bg-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-amber-800">Previsão de Recebimento</Label>
                      <Input
                        type="date"
                        value={paymentData.remaining_balance_forecast}
                        onChange={(e) => setPaymentData({...paymentData, remaining_balance_forecast: e.target.value})}
                        className="h-8 text-sm bg-white"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                value={paymentData.notes}
                onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                rows={2}
              />
            </div>
            
            {/* Opção para não criar transação bancária automática */}
            <div className="flex items-center space-x-2 pt-2 border-t">
              <Checkbox 
                id="skip_bank_txn_receivable"
                checked={paymentData.skip_bank_transaction}
                onCheckedChange={(checked) => setPaymentData({...paymentData, skip_bank_transaction: checked})}
              />
              <label 
                htmlFor="skip_bank_txn_receivable" 
                className="text-sm text-muted-foreground cursor-pointer"
              >
                Não criar transação bancária automática (uso extrato importado)
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsReceiveOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSubmitting || !paymentData.bank_account_id}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirmar Recebimento
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Customer Modal */}
      <Dialog open={isCustomerModalOpen} onOpenChange={setIsCustomerModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Cliente</DialogTitle>
            <DialogDescription>Cadastre um novo cliente. Digite o CNPJ para preencher automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* CNPJ with auto-lookup */}
            <div className="space-y-2">
              <Label>CNPJ/CPF</Label>
              <div className="flex gap-2">
                <Input
                  value={newCustomer.document}
                  onChange={(e) => setNewCustomer({...newCustomer, document: e.target.value})}
                  placeholder="00.000.000/0000-00"
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => handleCnpjLookup(newCustomer.document)}
                  disabled={isLookingUpCnpj || !newCustomer.document}
                >
                  {isLookingUpCnpj ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Razão Social */}
            <div className="space-y-2">
              <Label>Razão Social *</Label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                required
              />
            </div>

            {/* Nome Fantasia */}
            <div className="space-y-2">
              <Label>Nome Fantasia</Label>
              <Input
                value={newCustomer.trade_name}
                onChange={(e) => setNewCustomer({...newCustomer, trade_name: e.target.value})}
              />
            </div>

            {/* Email e Telefone */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                />
              </div>
            </div>

            <Separator />

            {/* CEP with auto-lookup */}
            <div className="space-y-2">
              <Label>CEP</Label>
              <div className="flex gap-2">
                <Input
                  value={newCustomer.cep}
                  onChange={(e) => setNewCustomer({...newCustomer, cep: e.target.value})}
                  placeholder="00000-000"
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => handleCepLookup(newCustomer.cep)}
                  disabled={isLookingUpCep || !newCustomer.cep}
                >
                  {isLookingUpCep ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Endereço */}
            <div className="space-y-2">
              <Label>Logradouro</Label>
              <Input
                value={newCustomer.address}
                onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
              />
            </div>

            {/* Número e Complemento */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Número</Label>
                <Input
                  value={newCustomer.address_number}
                  onChange={(e) => setNewCustomer({...newCustomer, address_number: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Complemento</Label>
                <Input
                  value={newCustomer.address_complement}
                  onChange={(e) => setNewCustomer({...newCustomer, address_complement: e.target.value})}
                />
              </div>
            </div>

            {/* Bairro, Cidade, Estado */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Bairro</Label>
                <Input
                  value={newCustomer.neighborhood}
                  onChange={(e) => setNewCustomer({...newCustomer, neighborhood: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Cidade</Label>
                <Input
                  value={newCustomer.city}
                  onChange={(e) => setNewCustomer({...newCustomer, city: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>UF</Label>
                <Input
                  value={newCustomer.state}
                  onChange={(e) => setNewCustomer({...newCustomer, state: e.target.value})}
                  maxLength={2}
                />
              </div>
            </div>

            <Separator />

            {/* ISS Settings */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="space-y-0.5">
                <Label className="text-sm">Cliente normalmente retém ISS</Label>
                <p className="text-xs text-muted-foreground">Preencher automaticamente nas novas contas</p>
              </div>
              <Switch
                checked={newCustomer.iss_retention_default}
                onCheckedChange={(checked) => setNewCustomer({...newCustomer, iss_retention_default: checked})}
              />
            </div>

            {newCustomer.iss_retention_default && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Município ISS</Label>
                  <Input
                    value={newCustomer.iss_municipality}
                    onChange={(e) => setNewCustomer({...newCustomer, iss_municipality: e.target.value})}
                    placeholder="Ex: São Paulo/SP"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Alíquota Padrão (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newCustomer.iss_default_rate}
                    onChange={(e) => setNewCustomer({...newCustomer, iss_default_rate: e.target.value})}
                    placeholder="Ex: 5.00"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsCustomerModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateCustomer} disabled={!newCustomer.name}>
              Criar Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Category Modal */}
      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Nova Categoria de Receita</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome da Categoria *</Label>
              <Input 
                value={newCategoryName} 
                onChange={(e) => setNewCategoryName(e.target.value)} 
                placeholder="Ex: Prestação de Serviço"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsCategoryModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateCategory} disabled={!newCategoryName.trim()}>Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalhes da Conta a Receber</DialogTitle>
          </DialogHeader>
          {selectedReceivable && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Cliente</span>
                  <p className="font-medium">{selectedReceivable.customer_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Natureza</span>
                  <p className="font-medium">
                    {OPERATION_NATURES.find(n => n.value === selectedReceivable.operation_nature)?.label || selectedReceivable.operation_nature}
                  </p>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Descrição</span>
                  <p className="font-medium">{selectedReceivable.description}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Vencimento</span>
                  <p className="font-medium">{formatDate(selectedReceivable.due_date)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Valor Bruto</span>
                  <p className="font-medium font-mono">{formatCurrency(selectedReceivable.gross_amount || selectedReceivable.amount)}</p>
                </div>
                {selectedReceivable.total_retained > 0 && (
                  <>
                    <div>
                      <span className="text-muted-foreground">ISS Retido</span>
                      <p className="font-medium font-mono text-red-600">- {formatCurrency(selectedReceivable.total_retained)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Valor Líquido</span>
                      <p className="font-medium font-mono text-green-600">{formatCurrency(selectedReceivable.net_expected_amount)}</p>
                    </div>
                  </>
                )}
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={getStatusBadgeClass(selectedReceivable.status)}>
                    {getStatusLabel(selectedReceivable.status)}
                  </Badge>
                </div>
                {selectedReceivable.payment_date && (
                  <>
                    <div>
                      <span className="text-muted-foreground">Data Recebimento</span>
                      <p className="font-medium">{formatDate(selectedReceivable.payment_date)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Conta Bancária</span>
                      <p className="font-medium">{selectedReceivable.bank_account_name || '-'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Valor Recebido</span>
                      <p className="font-medium font-mono">{formatCurrency(selectedReceivable.received_amount)}</p>
                    </div>
                  </>
                )}
                {selectedReceivable.notes && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Observações</span>
                    <p className="font-medium">{selectedReceivable.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Conta a Receber</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta conta?
              <div className="mt-2 p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedReceivable?.description}</p>
                <p className="text-sm">{selectedReceivable?.customer_name} • {formatCurrency(selectedReceivable?.gross_amount || selectedReceivable?.amount || 0)}</p>
              </div>
              <p className="mt-2 text-amber-600">Esta ação não pode ser desfeita.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reverse Payment Confirmation Dialog */}
      <AlertDialog open={isReverseOpen} onOpenChange={setIsReverseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-600" />
              Estornar Recebimento
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-3">Esta ação irá reverter o recebimento, retornando a conta ao status "Pendente".</p>
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedReceivable?.description}</p>
                <p className="text-sm">{selectedReceivable?.customer_name} • {formatCurrency(selectedReceivable?.received_amount || selectedReceivable?.gross_amount || 0)}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Recebido em: {selectedReceivable?.received_date ? formatDate(selectedReceivable.received_date) : '-'}
                </p>
              </div>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                <p className="font-medium">O que acontecerá:</p>
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>O status voltará para "Pendente"</li>
                  <li>A data de recebimento será removida</li>
                  <li>A transação bancária automática será excluída (se existir)</li>
                  <li>Você poderá editar o valor e data novamente</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleReverse} className="bg-amber-600 hover:bg-amber-700" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Estorno
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Replicate Dialog */}
      <Dialog open={isReplicateOpen} onOpenChange={setIsReplicateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Replicar Conta a Receber</DialogTitle>
            <DialogDescription>
              Uma cópia desta conta será criada com a nova data de vencimento.
            </DialogDescription>
          </DialogHeader>
          {selectedReceivable && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedReceivable.description}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedReceivable.customer_name} • {formatCurrency(selectedReceivable.gross_amount || selectedReceivable.amount)}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Nova Data de Vencimento *</Label>
                <Input
                  type="date"
                  value={replicateDate}
                  onChange={(e) => setReplicateDate(e.target.value)}
                  data-testid="replicate-due-date"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReplicateOpen(false)}>Cancelar</Button>
            <Button onClick={handleReplicate} disabled={isSubmitting || !replicateDate}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Copy className="h-4 w-4 mr-2" />
              Replicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Receive Dialog (1 PIX → várias contas) */}
      <BulkReceiveDialog
        open={isBulkReceiveOpen}
        onClose={() => setIsBulkReceiveOpen(false)}
        selectedReceivables={receivables.filter(r => selectedItems.includes(r.id) && r.status !== 'received')}
        bankAccounts={bankAccounts}
        onSuccess={() => {
          setSelectedItems([]);
          fetchReceivables();
        }}
      />


      {/* Bulk Delete Dialog */}
      <AlertDialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Contas Selecionadas</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir {selectedItems.length} conta(s) a receber?
              <div className="mt-2 p-3 bg-muted rounded-lg">
                <p className="font-medium">Total: {formatCurrency(getSelectedTotal())}</p>
              </div>
              <p className="mt-2 text-amber-600">Esta ação não pode ser desfeita.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir {selectedItems.length} Conta(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar em Lote</DialogTitle>
            <DialogDescription>
              Atualize os campos abaixo para {selectedItems.length} conta(s) selecionada(s).
              Apenas os campos preenchidos serão atualizados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={bulkEditData.category_id} onValueChange={(v) => setBulkEditData({...bulkEditData, category_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Manter atual" />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.id).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Data de Previsão</Label>
              <Input
                type="date"
                value={bulkEditData.forecast_date}
                onChange={(e) => setBulkEditData({...bulkEditData, forecast_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Data de Competência</Label>
              <Input
                type="date"
                value={bulkEditData.competence_date}
                onChange={(e) => setBulkEditData({...bulkEditData, competence_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                value={bulkEditData.notes}
                onChange={(e) => setBulkEditData({...bulkEditData, notes: e.target.value})}
                placeholder="Adicionar às observações existentes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkEditOpen(false)}>Cancelar</Button>
            <Button onClick={handleBulkEdit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Atualizar {selectedItems.length} Conta(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Installment Group Dialog */}
      <Dialog open={isInstallmentEditOpen} onOpenChange={setIsInstallmentEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Parcelas</DialogTitle>
            <DialogDescription>
              Parcela {selectedReceivable?.installment_number}/{selectedReceivable?.total_installments} - {selectedReceivable?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Escopo da alteração</Label>
              <Select value={installmentEditData.scope} onValueChange={(v) => setInstallmentEditData({...installmentEditData, scope: v})}>
                <SelectTrigger data-testid="edit-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Somente esta parcela</SelectItem>
                  <SelectItem value="future">Esta e as próximas (não recebidas)</SelectItem>
                  <SelectItem value="all_unpaid">Todas não recebidas do grupo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div className="text-sm font-medium">Campos a alterar (deixe em branco para manter)</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Input 
                  value={installmentEditData.customer_name} 
                  onChange={(e) => setInstallmentEditData({...installmentEditData, customer_name: e.target.value})} 
                  placeholder="Manter atual" 
                />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select 
                  value={installmentEditData.category_id} 
                  onValueChange={(v) => setInstallmentEditData({...installmentEditData, category_id: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Manter atual" /></SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c.type === 'income' && c.id).map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input 
                value={installmentEditData.description} 
                onChange={(e) => setInstallmentEditData({...installmentEditData, description: e.target.value})} 
                placeholder="Manter atual" 
              />
            </div>
            {installmentEditData.scope !== 'single' && (
              <div className="space-y-2">
                <Label>Novo valor por parcela (R$) - só para não recebidas</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={installmentEditData.amount} 
                  onChange={(e) => setInstallmentEditData({...installmentEditData, amount: e.target.value})} 
                  placeholder="Manter atual" 
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea 
                value={installmentEditData.notes} 
                onChange={(e) => setInstallmentEditData({...installmentEditData, notes: e.target.value})} 
                rows={2} 
                placeholder="Manter atual" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsInstallmentEditOpen(false)}>Cancelar</Button>
            <Button onClick={handleInstallmentEdit} disabled={isSubmitting} data-testid="save-installment-edit-btn">
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Aplicar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Create Cost Center Dialog */}
      <Dialog open={isCreateCostCenterOpen} onOpenChange={setIsCreateCostCenterOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Centro de Custo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={newCostCenterName}
                onChange={(e) => setNewCostCenterName(e.target.value)}
                placeholder="Ex: Operacional, Comercial..."
                data-testid="input-new-cost-center-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateCostCenterOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateCostCenter} data-testid="btn-save-cost-center">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceivablesPage;
